import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { toggleUnFollowConfirmBox } from 'store/actions/confirmBox'
import { selectIsLoggedIn } from 'store/selectors'
import {
  logEvent,
  snowplowEvent,
  userToUserContextSchemaData,
} from 'lib/analytics'
import { User, UserFollowLogKey } from 'lib/_types'
import { useCheckAuthorizeBeforeAction } from 'hooks'
import {
  useCurrentUser,
  useFollowUserMutation,
  useUnFollowUserMutation,
} from 'store/api/yay/user'

import Button from 'components/Button'

type FollowButton = {
  confirmBeforeUnfollow?: boolean
  followCallback?: (res: number) => void
  unfollowCallback?: (res: number) => void
  user: User
  from?: UserFollowLogKey
}

const FollowButton = ({
  confirmBeforeUnfollow = false,
  followCallback,
  unfollowCallback,
  user,
  from = 'undefined',
}: FollowButton) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const isLoggedIn = useSelector(selectIsLoggedIn)
  const { data: currentUser } = useCurrentUser()

  const checkAuthorizeBeforeAction = useCheckAuthorizeBeforeAction()

  const [followerUser] = useFollowUserMutation()
  const [unFollowerUser] = useUnFollowUserMutation()

  const onFollowUser = () => {
    logEvent.user_follow({
      approval: user?.is_private ? 'requested' : 'none',
      target_user_id: user?.id?.toString(),
      from,
    })

    followerUser(user?.id).then((res) => {
      if ((res?.['data']?.result ?? '') == 'success') {
        snowplowEvent.follow({
          user: userToUserContextSchemaData({ user }),
        })
        followCallback?.(user?.id)
      }
    })
  }

  const onUnfollowUser = (_confirm: boolean) => {
    if (_confirm) {
      dispatch(
        toggleUnFollowConfirmBox({
          user,
        })
      )
    } else {
      logEvent.user_unfollow({
        target_user_id: user?.id?.toString(),
        from,
      })

      unFollowerUser(user?.id).then((res) => {
        if ((res?.['data']?.result ?? '') == 'success') {
          unfollowCallback?.(user?.id)
        }
      })
    }
  }

  if (user?.id === currentUser?.id) {
    return null
  } else if (!isLoggedIn) {
    return (
      <Button
        className="FollowButton"
        buttonStyle="bordered-secondary-without-bg"
        icon="plus-brown"
        bold
        onClick={checkAuthorizeBeforeAction(() => {})}
      >
        {t('common.follow')}
      </Button>
    )
  } else if (user?.follow_pending) {
    return (
      <Button
        className="FollowButton"
        buttonStyle="gray"
        icon="clock"
        onClick={() => onUnfollowUser(false)}
        bold
      >
        {t('follow_button.follow_pending')}
      </Button>
    )
  } else if (user?.following) {
    return (
      <Button
        className="FollowButton"
        buttonStyle="secondary"
        icon="check"
        onClick={() => onUnfollowUser(confirmBeforeUnfollow)}
        bold
      >
        {t('common.following')}
      </Button>
    )
  } else {
    return (
      <Button
        className="FollowButton"
        buttonStyle="bordered-secondary-without-bg"
        icon={user?.is_private ? 'lock-brown' : 'plus-brown'}
        onClick={onFollowUser}
        bold
      >
        {t('common.follow')}
      </Button>
    )
  }
}

export default FollowButton
